import { makeObservable, computed, observable, action, runInAction } from 'mobx';
import { CancelToken } from 'axios';
import { Subscription } from 'rxjs';
import { ContractsIngestionService } from '../services';
import { InputBaseModel, ContractComparisonModel, NodeBaseModel } from '../models';
import { SourceStore } from '../../custom_shared/stores';
import { ActionParams, FetchPreviousContractDataRequest, ContractComparisonParameters, NodeBaseDto } from '../types';
import { NodeUtils, InstanceUtils } from '../misc';
import AppStore from './AppStore';
import SessionStore from './SessionStore';
import ActionStore from './ActionStore';
import NodesStore from './NodesStore';
import InputsStore from './InputsStore';
import SettingsStore from './SettingsStore';
import { ContractComparisonFilter } from '../misc/ContractComparisonFilterUtils';

type ContractComparisonFilterOption = {
    label: string;
    value: ContractComparisonFilter;
};

export default class ContractComparisonStore {
    readonly filterOptions: ContractComparisonFilterOption[] = [
        { label: 'All Fields', value: 'all' },
        { label: 'Only with Changes', value: 'onlyWithChanges' },
        { label: 'Without Changes', value: 'withoutChanges' },
        { label: 'Added', value: 'added' },
        { label: 'Removed', value: 'removed' },
        { label: 'Modified', value: 'modified' },
        { label: 'Unsaved Changes', value: 'unsaved' }
    ];

    selectedFilter: ContractComparisonFilter = 'all';

    contractComparisons: Map<string, ContractComparisonModel> = new Map();

    private readonly baseCompanyChangeSubscription: Subscription;

    constructor(
        private readonly appStore: AppStore,
        private readonly sessionStore: SessionStore,
        private readonly actionStore: ActionStore,
        private readonly nodesStore: NodesStore,
        private readonly inputsStore: InputsStore,
        private readonly sourceStore: SourceStore,
        private readonly settingStore: SettingsStore,
        private readonly contractsIngestionService: ContractsIngestionService
    ) {
        makeObservable(this, {
            selectedFilter: observable,
            contractComparisons: observable,
            setSelectedFilter: action,
            resetContractComparisons: action,
            isContractComparisonSupported: computed,
            selectedContractComparison: computed,
            selectedFilterOption: computed,
            getOrCreateContractComparison: action,
            isComparisonSummaryConfigured: computed
        });

        this.baseCompanyChangeSubscription = this.appStore.baseCompanySegregationStore.baseCompanyChangeSubject.subscribe(() => {
            this.resetContractComparisons();
        });
    }

    setSelectedFilter(selectedFilter: ContractComparisonFilter) {
        this.selectedFilter = selectedFilter;
    }

    get isContractComparisonSupported() {
        // TODO: add sections support
        if (!this.settingStore.isBasicLayoutSettings && !this.settingStore.isLayersLayoutSettings) {
            return false;
        }

        return true;
    }

    get selectedContractComparison() {
        return Array.from(this.contractComparisons.values()).find(contractComparison => contractComparison.isSelected);
    }

    get selectedFilterOption() {
        return this.filterOptions.find(option => option.value === this.selectedFilter);
    }

    get isComparisonSummaryConfigured() {
        return this.sessionStore.response?.isComparisonSummaryConfigured ?? false;
    }

    resetContractComparisons() {
        this.contractComparisons.forEach(contractComparison => contractComparison.reset());

        runInAction(() => {
            this.selectedFilter = 'all';
        });
    }

    getOrCreateContractComparison(businessIdInput: InputBaseModel, parameters: ContractComparisonParameters = {}) {
        const existingContractComparison = this.contractComparisons.get(businessIdInput.guid);

        if (existingContractComparison) {
            return existingContractComparison;
        }

        const contractComparisonModel = new ContractComparisonModel(
            this,
            this.sourceStore,
            businessIdInput,
            parameters
        );

        this.contractComparisons.set(businessIdInput.guid, contractComparisonModel);

        return contractComparisonModel;
    }

    async fetchPreviousContractData(request: FetchPreviousContractDataRequest, cancelToken?: CancelToken) {
        return await this.contractsIngestionService.fetchPreviousContractData(
            this.appStore.sessionExtension,
            this.appStore.sessionId,
            request,
            cancelToken
        );
    }

    async executeContractComparisonAction(nodes: NodeBaseModel[], actionId: string, actionParams: ActionParams = {}) {
        const runtimeData = ActionStore.getRuntimeData(nodes, this.settingStore.layoutSettings, actionParams);

        return await this.contractsIngestionService.executeContractComparisonAction(
            this.appStore.sessionExtension,
            this.appStore.sessionId,
            actionId,
            runtimeData
        );
    }

    async generateContractComparisonSummary(payload: string, cancelToken?: CancelToken) {
        return await this.contractsIngestionService.generateContractComparisonSummary(
            this.appStore.sessionExtension,
            this.appStore.sessionId,
            payload,
            cancelToken
        );
    }

    openOrLoadContractComparison(contractComparison: ContractComparisonModel) {
        contractComparison.openOrLoad(NodeUtils.copyNodeDtos(this.nodesStore.nodes)).catch(error => {
            console.error('Error while opening contract comparison: ', error);
        });
    }

    saveContractComparisonChanges(contractComparison: ContractComparisonModel) {
        const nodes = contractComparison.newContractNodes
            .map(comparisonNode => {
                const contractNode = this.nodesStore.nodes.find(node => node.id === comparisonNode.id);

                if (!contractNode) {
                    return null;
                }

                // Basic node can be fully replaced
                if (InstanceUtils.isBasicNode(comparisonNode)) {
                    return comparisonNode.dto;
                }

                // Replace only selected layer for layers node
                if (InstanceUtils.isLayersNode(contractNode) && InstanceUtils.isLayersNode(comparisonNode)) {
                    contractNode.layers.forEach(contractLayer => {
                        const comparisonLayer = comparisonNode.layers.find(layer => layer.id === contractLayer.id);

                        if (comparisonLayer) {
                            contractNode.replaceLayer(comparisonLayer.dto, this.sourceStore.sources);
                        }
                    });

                    return contractNode.dto;
                }

                // Fallback to original node, other nodes are not supported yet
                return contractNode.dto;
            })
            .filter((node): node is NodeBaseDto => node !== null);

        this.nodesStore.createOrUpdateNodes(NodeUtils.deepCloneNodeDtos(nodes));
        this.inputsStore.inputsWithSource.forEach(input => input.fetchSourceOptions());
        this.actionStore.rebuildNodes();
    }

    dispose() {
        this.baseCompanyChangeSubscription.unsubscribe();
        this.contractComparisons.forEach(contractComparison => contractComparison.dispose());

        runInAction(() => {
            this.contractComparisons.clear();
            this.selectedFilter = 'all';
        });
    }
}
